import styled from '@emotion/styled';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useContext, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  NotesRichTextEditor,
  parseMentions,
} from '__components/Inputs/NotesRichTextEditor';
import { useAttachListener } from 'hooks/useAttachListener';
import { NotesLabel } from 'pages/Common/styles/Profile';

import { FORMDATA_FIELDS } from '../config';
import { ActivityFields, StyledSection } from '../styled';
import ActivityService from 'services/ActivityService';

import { isDefaultUndeletableNotesField } from 'checks/fields';
import FieldInput from '__components/Fields/FieldInput';
import { getDataQAForInput } from 'components/Inputs/helpers';
import { fieldModifier } from '../helpers';
import { isEmailStatusField } from 'checks/fields';
import { getPercentageChanceToCloseTooltip } from 'pages/CustomObjects/RecordsPage/helpers';
import { ACTIVITY_FIELD_VALUE_FILE_UPLOAD_SOURCE } from 'hooks/uploadFiles/useUploadFile';
import { isContact } from 'utility/fieldHelpers';
import { _modalSize } from 'components/Modals';
import { OBJECT_ASSOCATIONS } from 'components/GenericWizard/wizards/scheduleActivity';
import { useCompleteActivityWizardContext } from '../context';
import { PluginContext } from 'ts-components/Plugins/PluginContext';
import { getFieldsDescription } from 'components/Fields/FieldInput/helpers';
import { DESCRIPTION_VISIBILITY } from 'utility/constants';
import { TextViewer } from '__components/WYSIWYG';

type LayoutItem =
  | { type: 'field'; columns: number; fieldId: string; id: string }
  | { type: 'section'; columns: number; id: string; label: string };

type CompleteActivityFieldsProps = {
  activityId: string;
  associationMode:
    | 'selected_objects_associated'
    | 'all_objects_associated'
    | 'no_objects_associated';
  errors: boolean[];
  showErrors: (boolean | { value: any[] })[];
  getKeyListenersProps(id: string): {
    fieldId: string;
    shouldUseKeyboardEventHandle: boolean;
    onBlur(): void;
    onFocus(id: string): void;
  };
  setShowErrors: Dispatch<SetStateAction<(boolean | { value: any[] })[]>>;
};

const { NOTES, MENTIONS, FIELDS } = FORMDATA_FIELDS;

const upToParent = (el: any, id: string) => {
  while (el && el.parentNode) {
    el = el.parentNode;
    if (el?.id === id) {
      return el;
    }
  }
  // Many DOM methods return null if they don't
  // find the element they are searching for
  // It would be OK to omit the following and just
  // return undefined
  return null;
};

const LayoutItemContainer = styled.div`
  margin-bottom: 20px;
`;

const CompleteActivityFields = (props: CompleteActivityFieldsProps) => {
  const {
    associationMode,
    activityId,
    errors,
    showErrors,
    getKeyListenersProps,
    setShowErrors,
  } = props;

  const {
    activity,
    activityFields,
    formData,
    isRuleActiveForField,
    updateField,
  } = useCompleteActivityWizardContext();

  const { fields, notes, relatedObjects } = formData;

  const fieldsById = useMemo(() => {
    return activityFields.reduce<Record<string, any>>((acc, field) => {
      acc[field.id] = field;
      return acc;
    }, {});
  }, [activityFields]);

  const { t } = useTranslation();
  const menuOpenRef = useRef(false);
  const { dataAdornments } = useContext(PluginContext);

  const { shouldDisableField } = useCompleteActivityWizardContext();

  const setField = useCallback(
    (id: string, value: any) =>
      updateField(
        FIELDS,
        (prev: any) =>
          prev.map((prevField: any) =>
            prevField.id === id ? { ...prevField, value } : prevField
          ),
        false
      ),
    [updateField]
  );

  const scrollDropdownListener = useCallback((ev: any) => {
    if (menuOpenRef.current && !upToParent(ev.target, 'dropdown-menu')) {
      ev.target?.parentNode?.parentNode?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, []);

  const listenerRef = useAttachListener(scrollDropdownListener, 'mouseup');

  if (!activity || !activityFields.length) {
    return null;
  }

  return (
    <StyledSection
      index={
        associationMode === OBJECT_ASSOCATIONS.NO_OBJECT_ASSOCIATIONS ? 0 : 1
      }
      header={t('Complete Activity')}
      isActive
      isLastIndex
      ref={listenerRef}
    >
      <ActivityFields>
        {activity.layout.map((item: LayoutItem, i: number) => {
          if (item.type === 'field') {
            const field = fieldsById[item.fieldId];
            if (field.isHidden || !isRuleActiveForField(field)) {
              return null;
            }
            const customObjectField = field.customObjectField;
            // we have to left only custom activity field associated to entities & activity fields
            const fieldWithvalue = fields.find(
              ({ id, customObject, value }) =>
                id === field.id &&
                (!customObject ||
                  relatedObjects.some(
                    (obj) => obj.customObject === customObject
                  ) ||
                  value)
            );

            let error = null;
            const errorSource = showErrors[i] || errors[i];
            if (showErrors.length) {
              if (errorSource && typeof errorSource === 'object') {
                error = { message: errorSource.value };
              } else if (errorSource) {
                error = { message: t('This field is required.') };
              }
            }

            const isFieldDisabled =
              shouldDisableField(field) ||
              !!(
                isEmailStatusField(field) &&
                customObjectField !== null &&
                isContact(customObjectField.customObject) &&
                relatedObjects.find(
                  ({ customObject }) =>
                    customObjectField!.customObject.id === customObject
                ) &&
                field.options.find(
                  ({ id }: any) => fieldWithvalue?.value === id
                )?.code === 'suppression_list'
              );

            const percentageChanceToCloseProps = {
              ...(fieldWithvalue?.valuePlaceholder &&
              !fieldWithvalue?.value &&
              fieldWithvalue?.value !== 0
                ? {
                    info: getPercentageChanceToCloseTooltip(t),
                    placeholder: fieldWithvalue?.valuePlaceholder,
                  }
                : {}),
            };
            let fieldTypeProps = {};

            if (field.fieldType === 'longtext') {
              fieldTypeProps = {
                initialHeight: 106, // 5 lines of text
              };
            }
            const { withNameDescription } = getFieldsDescription(field, [
              DESCRIPTION_VISIBILITY[0].value,
              DESCRIPTION_VISIBILITY[1].value,
            ]);
            const additionalProps = {
              labelInfo: withNameDescription,
              description: withNameDescription,
            };

            let fetchUrl = customObjectField?.customObject?.fetchUrl;
            if (fetchUrl && ['pipeline', 'standard'].includes(fetchUrl)) {
              fetchUrl = `custom-objects/${customObjectField!.customObject.id}`;
            }

            return (
              <LayoutItemContainer
                key={item.fieldId}
                className={item.columns === 2 ? 'span2' : 'span1'}
              >
                {isDefaultUndeletableNotesField(field) ? (
                  <NotesRichTextEditor
                    {...getKeyListenersProps(field.id)}
                    label={
                      <NotesLabel>
                        {t('Notes')}
                        {field.isRequired && '*'}
                      </NotesLabel>
                    }
                    value={notes}
                    onChange={({ editor }: any) => {
                      const text = editor.getHTML();
                      updateField(NOTES, text);
                      updateField(MENTIONS, parseMentions(text));
                    }}
                  />
                ) : (
                  <FieldInput
                    portalDateInput
                    key={isFieldDisabled}
                    source={ACTIVITY_FIELD_VALUE_FILE_UPLOAD_SOURCE}
                    isActivityField
                    field={fieldModifier(field, relatedObjects)}
                    disabled={isFieldDisabled}
                    adornments={dataAdornments}
                    fetchUrl={fetchUrl}
                    value={fieldWithvalue?.value ?? undefined}
                    onChange={(val: any) => {
                      if (showErrors[i]) {
                        setShowErrors(
                          showErrors.map((err, j) => (i === j ? false : err))
                        );
                      }
                      setField(field.id, val);
                    }}
                    isGetFullObject
                    underline={false}
                    error={error}
                    serviceToUse={ActivityService}
                    objectId={activityId}
                    isShowAsterisk={field.isRequired}
                    required={field.isRequired}
                    inModal
                    {...getDataQAForInput(
                      `activity-${field.name}`,
                      field.fieldType
                    )}
                    onMenuOpen={() => (menuOpenRef.current = true)}
                    onMenuClose={() => (menuOpenRef.current = false)}
                    {...additionalProps}
                    {...percentageChanceToCloseProps}
                    {...getKeyListenersProps(field.id)}
                    {...fieldTypeProps}
                  />
                )}
              </LayoutItemContainer>
            );
          }

          return (
            <LayoutItemContainer
              key={item.id}
              className={item.columns === 2 ? 'span2' : 'span1'}
            >
              <TextViewer content={item.label} />
            </LayoutItemContainer>
          );
        })}
      </ActivityFields>
    </StyledSection>
  );
};

export default CompleteActivityFields;
